import { CancelOrderModal } from '@pages/Trade/Portfolio/Modals/CancelOrderModal'
import { getStepDecimals } from '@pages/Trade/utils/helpers'

import { useModal } from '@context/ModalsContext'

import { SensitiveData } from '@ui/Privacy/SensitiveData'
import { Body1, Caption1, Caption2 } from '@ui/Typography'
import { Badge } from '@ui/components/Badge/Badge'

import { useFormatNumber } from '@hooks/useFormatNumber'

import { Box } from '@shared/components/Box/Box'

import { TradeDetailsPendingModal } from '../Modals/TradeDetailsPendingModal'
import { useFixTradingWebSocketContext } from '../context/FixTradingWebSocketContext'
import type { DxtradeInstrumentMap } from '../hooks/useDxtradeInstruments'
import type { PendingOrderWithLots } from '../hooks/usePendingOrdersWithLots'
import {
  formatSymbol,
  getOpenLegTargetPrice,
  renderSide,
  upperCaseFirstLetter,
} from '../utils/formatters'
import { getCancelOrderParams } from '../utils/orderHelpers'
import { LivePriceCell } from './LivePriceCell'
import { TradeCardList } from './TradeCardList'
import { TradeCardRow } from './TradeCardRow'

import styles from './CardTradingView.module.scss'

interface Props {
  orders: PendingOrderWithLots[]
  isLoading: boolean
  accountCode?: string
  dxtradeInstruments?: DxtradeInstrumentMap
  hasActiveFilters?: boolean
}

export const CardPendingView: React.FC<Props> = ({
  orders,
  isLoading,
  accountCode,
  dxtradeInstruments,
  hasActiveFilters,
}) => {
  const { cancelOrder, newOrder, changePendingOrderSlTp, replaceOrder } =
    useFixTradingWebSocketContext()
  const { formatNumber } = useFormatNumber()
  const detailsModal = useModal(TradeDetailsPendingModal)
  const cancelOrderModal = useModal(CancelOrderModal)
  return (
    <TradeCardList
      isLoading={isLoading}
      isEmpty={orders.length === 0}
      hasActiveFilters={hasActiveFilters}
    >
      {orders.map((order) => {
        const sizeLots = typeof order.sizeLots === 'number' ? order.sizeLots : undefined
        const targetPrice = getOpenLegTargetPrice(order)
        const priceIncrement = dxtradeInstruments?.[order.instrument || '']?.priceIncrement
        const fractionDigits = getStepDecimals(priceIncrement)
        const formattedTarget =
          targetPrice != null
            ? formatNumber(targetPrice, {
                minDecimalPlaces: fractionDigits,
                maxDecimalPlaces: fractionDigits,
              })
            : '-'

        return (
          <TradeCardRow
            key={order.orderId}
            sideLabel={formatSymbol(order.instrument ?? '', order.description)}
            rightPrimary={
              targetPrice != null ? (
                <SensitiveData>
                  <Body1 medium>{formattedTarget}</Body1>
                </SensitiveData>
              ) : undefined
            }
            caption={
              <Box flex justify='space-between' align='center'>
                <Box flex gap='x4'>
                  {renderSide(order.side)}
                  {order.type && <Caption1>{upperCaseFirstLetter(order.type)}</Caption1>}
                  {sizeLots != null && (
                    <Caption1>
                      <SensitiveData>{sizeLots}</SensitiveData>
                    </Caption1>
                  )}
                </Box>
                <SensitiveData>
                  <LivePriceCell
                    trade={{ symbol: order.instrument ?? '', side: order.side, priceIncrement }}
                    priceType='order'
                    TypographyComp={Caption1}
                    defaultColor='text-med-em'
                  />
                </SensitiveData>
              </Box>
            }
            meta={
              order.stopLoss != null || order.takeProfit != null ? (
                <Box flex gap='x8' mt='x4'>
                  {order.stopLoss != null && (
                    <Badge type='black' size='xxs' contrast='low'>
                      <Caption2 color='text-med-em'>SL</Caption2>
                      &nbsp;
                      <Caption2 color='text-med-em'>
                        <SensitiveData>
                          {formatNumber(order.stopLoss, {
                            minDecimalPlaces: fractionDigits,
                            maxDecimalPlaces: fractionDigits,
                          }) ?? '-'}
                        </SensitiveData>
                      </Caption2>
                    </Badge>
                  )}
                  {order.takeProfit != null && (
                    <Badge type='black' size='xxs' contrast='low'>
                      <Caption2 color='text-med-em'>TP</Caption2>
                      &nbsp;
                      <Caption2 color='text-med-em'>
                        <SensitiveData>
                          {formatNumber(order.takeProfit, {
                            minDecimalPlaces: fractionDigits,
                            maxDecimalPlaces: fractionDigits,
                          }) ?? '-'}
                        </SensitiveData>
                      </Caption2>
                    </Badge>
                  )}
                </Box>
              ) : undefined
            }
            className={styles.pendingRow}
            onClick={() =>
              detailsModal.open({
                instrumentMeta: {
                  pipSize: dxtradeInstruments?.[order.instrument || '']?.pipSize,
                  priceIncrement: dxtradeInstruments?.[order.instrument || '']?.priceIncrement,
                },
                order,
                accountCode,
                cancelOrder,
                newOrder,
                changePendingOrderSlTp,
                replaceOrder,
              })
            }
            onClose={() => {
              cancelOrderModal.open({
                order,
                priceIncrement,
                onConfirm: (onSettled) => {
                  const params = getCancelOrderParams(order)
                  if (!params) return false
                  return Boolean(cancelOrder({ ...params, onSettled }))
                },
              })
            }}
          />
        )
      })}
    </TradeCardList>
  )
}

export default CardPendingView
